import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ComparisonActions = ({ 
  selectedCranes, 
  onGenerateReport, 
  onSaveComparison, 
  onClearAll,
  onExportData 
}) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [comparisonName, setComparisonName] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const handleSaveComparison = () => {
    if (comparisonName?.trim()) {
      onSaveComparison({
        name: comparisonName,
        cranes: selectedCranes,
        createdAt: new Date()?.toISOString()
      });
      setComparisonName('');
      setShowSaveDialog(false);
    }
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      await onGenerateReport();
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const exportOptions = [
    { label: 'PDF Report', icon: 'FileText', action: handleGenerateReport },
    { label: 'Excel Export', icon: 'Download', action: () => onExportData('excel') },
    { label: 'CSV Export', icon: 'Download', action: () => onExportData('csv') }
  ];

  const quickActions = [
    { 
      label: 'Save Comparison', 
      icon: 'Bookmark', 
      action: () => setShowSaveDialog(true),
      disabled: selectedCranes?.length === 0
    },
    { 
      label: 'Clear All', 
      icon: 'Trash2', 
      action: onClearAll,
      variant: 'outline',
      disabled: selectedCranes?.length === 0
    },
    { 
      label: 'Share', 
      icon: 'Share2', 
      action: () => console.log('Share comparison'),
      variant: 'ghost',
      disabled: selectedCranes?.length === 0
    }
  ];

  return (
    <>
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-medium text-text-primary mb-4">Comparison Actions</h3>
        
        {/* Export Options */}
        <div className="space-y-3 mb-6">
          <h4 className="text-sm font-medium text-text-secondary">Export & Reports</h4>
          {exportOptions?.map((option, index) => (
            <Button
              key={index}
              variant={index === 0 ? "default" : "outline"}
              size="sm"
              onClick={option?.action}
              iconName={option?.icon}
              iconPosition="left"
              fullWidth
              disabled={selectedCranes?.length === 0}
              loading={index === 0 && isGeneratingReport}
            >
              {option?.label}
            </Button>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="space-y-3 mb-6">
          <h4 className="text-sm font-medium text-text-secondary">Quick Actions</h4>
          {quickActions?.map((action, index) => (
            <Button
              key={index}
              variant={action?.variant || "outline"}
              size="sm"
              onClick={action?.action}
              iconName={action?.icon}
              iconPosition="left"
              fullWidth
              disabled={action?.disabled}
            >
              {action?.label}
            </Button>
          ))}
        </div>

        {/* Comparison Stats */}
        {selectedCranes?.length > 0 && (
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-text-secondary mb-3">Comparison Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-secondary">Models Selected:</span>
                <span className="text-text-primary font-medium">{selectedCranes?.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Price Range:</span>
                <span className="text-text-primary font-medium">
                  ${Math.min(...selectedCranes?.map(c => parseInt(c?.price?.replace(/[$,]/g, ''))))?.toLocaleString()} - 
                  ${Math.max(...selectedCranes?.map(c => parseInt(c?.price?.replace(/[$,]/g, ''))))?.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Year Range:</span>
                <span className="text-text-primary font-medium">
                  {Math.min(...selectedCranes?.map(c => c?.year))} - {Math.max(...selectedCranes?.map(c => c?.year))}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Recent Comparisons */}
        <div className="pt-4 border-t border-border mt-6">
          <h4 className="text-sm font-medium text-text-secondary mb-3">Recent Comparisons</h4>
          <div className="space-y-2">
            {[
              { name: 'Mobile Cranes 200T+', date: '2025-11-05', count: 3 },
              { name: 'Liebherr vs Grove', date: '2025-11-04', count: 2 },
              { name: 'All-Terrain Analysis', date: '2025-11-03', count: 4 }
            ]?.map((comparison, index) => (
              <button
                key={index}
                className="w-full text-left p-2 rounded hover:bg-muted transition-smooth"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-primary truncate">
                    {comparison?.name}
                  </span>
                  <Icon name="ChevronRight" size={12} className="text-text-secondary" />
                </div>
                <div className="flex items-center justify-between text-xs text-text-secondary">
                  <span>{comparison?.count} models</span>
                  <span>{comparison?.date}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Save Dialog Modal */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-text-primary">Save Comparison</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSaveDialog(false)}
                iconName="X"
              />
            </div>
            
            <div className="space-y-4">
              <Input
                label="Comparison Name"
                type="text"
                placeholder="Enter a name for this comparison..."
                value={comparisonName}
                onChange={(e) => setComparisonName(e?.target?.value)}
                required
              />
              
              <div className="text-sm text-text-secondary">
                This comparison includes {selectedCranes?.length} crane models and will be saved to your account.
              </div>
              
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowSaveDialog(false)}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSaveComparison}
                  disabled={!comparisonName?.trim()}
                  fullWidth
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ComparisonActions;